// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — pages/Configuracoes/ConfiguracoesWhatsAppCloud.tsx
//  WhatsApp OFICIAL (Cloud API / Meta) — conexão da loja, flags de notificação,
//  envio de teste e criação (seed) dos templates na WABA.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Send, Loader2, MessageSquare, ShieldCheck, Sparkles, Plug } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { useWhatsAppCloud } from '@/features/whatsappCloud/useWhatsAppCloud';
import { conectarWhatsApp } from '@/features/whatsappCloud/embeddedSignup';
import { getApiErrorMessage } from '@/lib/apiClient';

type Flags = {
  notificarOs: boolean;
  notificarFinanceiro: boolean;
  notificarFiscal: boolean;
  notificarOrcamento: boolean;
  notificarEntrega: boolean;
  notificarCrm: boolean;
};

const FLAGS_INFO: { key: keyof Flags; label: string; hint: string }[] = [
  { key: 'notificarFiscal',    label: 'Nota fiscal autorizada', hint: 'NFC-e, NF-e e NFS-e emitidas.' },
  { key: 'notificarOs',        label: 'Ordem de serviço',       hint: 'Avisa quando a OS fica pronta.' },
  { key: 'notificarFinanceiro',label: 'Financeiro',             hint: 'Boleto disponível e lembretes de cobrança.' },
  { key: 'notificarOrcamento', label: 'Orçamento',              hint: 'Avisa quando o orçamento é criado.' },
  { key: 'notificarEntrega',   label: 'Entrega',                hint: 'Avisa quando o pedido é despachado.' },
  { key: 'notificarCrm',       label: 'CRM (relacionamento)',   hint: 'Aniversário, datas e promoções — MARKETING (exige opt-in do cliente).' },
];

export default function ConfiguracoesWhatsAppCloud() {
  const { query, salvar, teste, seedTemplates, embeddedConfig, onboarding } = useWhatsAppCloud();
  const [conectando, setConectando] = useState(false);

  const [wabaId, setWabaId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
  const [verifiedName, setVerifiedName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [tokenDigitado, setTokenDigitado] = useState(false);
  const [ativo, setAtivo] = useState(false);
  const [flags, setFlags] = useState<Flags>({
    notificarOs: true, notificarFinanceiro: true, notificarFiscal: true,
    notificarOrcamento: true, notificarEntrega: true, notificarCrm: false,
  });
  const [numeroTeste, setNumeroTeste] = useState('');
  const [boletoSegundaVia, setBoletoSegundaVia] = useState(false);
  const [boletoLembrete, setBoletoLembrete] = useState(false);
  const [boletoLembreteDias, setBoletoLembreteDias] = useState('2,0');

  useEffect(() => {
    const cfg = query.data;
    if (!cfg) return;
    setWabaId(cfg.wabaId ?? '');
    setPhoneNumberId(cfg.phoneNumberId ?? '');
    setDisplayPhoneNumber(cfg.displayPhoneNumber ?? '');
    setVerifiedName(cfg.verifiedName ?? '');
    setAtivo(cfg.ativo);
    setAccessToken('');
    setTokenDigitado(false);
    setFlags({
      notificarOs: cfg.notificarOs,
      notificarFinanceiro: cfg.notificarFinanceiro,
      notificarFiscal: cfg.notificarFiscal,
      notificarOrcamento: cfg.notificarOrcamento,
      notificarEntrega: cfg.notificarEntrega,
      notificarCrm: cfg.notificarCrm,
    });
    setBoletoSegundaVia(cfg.boletoSegundaVia ?? false);
    setBoletoLembrete(cfg.boletoLembrete ?? false);
    setBoletoLembreteDias(cfg.boletoLembreteDias || '2,0');
  }, [query.data]);

  const handleSalvar = async () => {
    try {
      await salvar.mutateAsync({
        wabaId: wabaId.trim() || null,
        phoneNumberId: phoneNumberId.trim() || null,
        accessToken: tokenDigitado ? accessToken.trim() : null,
        displayPhoneNumber: displayPhoneNumber.trim() || null,
        verifiedName: verifiedName.trim() || null,
        ativo,
        ...flags,
        boletoSegundaVia,
        boletoLembrete,
        boletoLembreteDias: boletoLembreteDias.trim() || '2,0',
      });
      toast.success('Configurações salvas.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleConectar = async () => {
    const cfg = embeddedConfig.data;
    if (!cfg?.appId || !cfg?.configId) {
      toast.error('Conexão automática indisponível: o App da Meta não está configurado no servidor.');
      return;
    }
    setConectando(true);
    try {
      const r = await conectarWhatsApp(cfg.appId, cfg.configId, cfg.sdkVersion);
      await onboarding.mutateAsync({ code: r.code, wabaId: r.wabaId, phoneNumberId: r.phoneNumberId });
      toast.success('WhatsApp conectado! As credenciais da loja foram preenchidas.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : getApiErrorMessage(err));
    } finally {
      setConectando(false);
    }
  };

  const handleTestar = async () => {
    if (!numeroTeste.trim()) { toast.error('Informe um número (DDI+DDD+número).'); return; }
    try {
      const r = await teste.mutateAsync({ numero: numeroTeste.trim() });
      if (r.ok) toast.success('Mensagem de teste enviada.');
      else toast.error('Falha no envio de teste.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleSeed = async () => {
    try {
      const r = await seedTemplates.mutateAsync();
      toast.success(`Templates: ${r.criados} criados, ${r.jaExistiam} já existiam, ${r.falharam} falhas.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  if (query.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const cfg = query.data;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold">WhatsApp Oficial (Cloud API)</h1>
          <p className="text-sm text-muted-foreground">
            Notificações via WhatsApp Business Platform (Meta). Conecte o número da loja e escolha o que avisar.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conexão da loja</CardTitle>
          <CardDescription>
            Conecte pela Meta em poucos cliques, ou informe os dados manualmente (deixe o token em branco para preservar o valor salvo).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Conexão automática (Embedded Signup da Meta) — equivalente ao Desktop. */}
          <div className="rounded-lg border bg-muted/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium">Conexão rápida</p>
              <p className="text-xs text-muted-foreground">
                Conecte o WhatsApp da sua empresa direto pela Meta — o sistema preenche WABA, número e token pra você.
                O número continua funcionando no app WhatsApp Business.
              </p>
            </div>
            <Button
              onClick={handleConectar}
              disabled={conectando || onboarding.isPending || !embeddedConfig.data?.appId}
              className="gap-2 bg-green-600 hover:bg-green-700 whitespace-nowrap"
            >
              {(conectando || onboarding.isPending)
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Plug className="h-4 w-4" />}
              Conectar WhatsApp
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">ou informe os dados manualmente</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>WABA ID</Label>
            <Input value={wabaId} onChange={(e) => setWabaId(e.target.value)} placeholder="ID da WhatsApp Business Account" />
          </div>
          <div className="space-y-1">
            <Label>Phone Number ID</Label>
            <Input value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} placeholder="ID do número remetente" />
          </div>
          <div className="space-y-1">
            <Label>Número (exibição)</Label>
            <Input value={displayPhoneNumber} onChange={(e) => setDisplayPhoneNumber(e.target.value)} placeholder="+55 34 9860-0650" />
          </div>
          <div className="space-y-1">
            <Label>Nome verificado</Label>
            <Input value={verifiedName} onChange={(e) => setVerifiedName(e.target.value)} placeholder="Nome exibido ao cliente" />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>
              Access Token
              {cfg?.tokenMascarado && (
                <span className="ml-2 text-xs font-mono text-muted-foreground">(atual: {cfg.tokenMascarado})</span>
              )}
            </Label>
            <Input
              type="password"
              value={accessToken}
              onChange={(e) => { setAccessToken(e.target.value); setTokenDigitado(true); }}
              placeholder={cfg?.temToken ? 'deixe em branco para manter' : 'token de acesso da Graph API'}
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between border-t pt-4">
            <div>
              <p className="text-sm font-medium">Integração ativa</p>
              <p className="text-xs text-muted-foreground">
                Desligada, nenhuma mensagem é enviada (as tentativas apenas registram log).
              </p>
            </div>
            <Switch checked={ativo} onCheckedChange={setAtivo} />
          </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">O que notificar</CardTitle>
          <CardDescription>Cada grupo liga/desliga um tipo de aviso automático ao cliente.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {FLAGS_INFO.map(({ key, label, hint }) => (
            <div key={key} className="flex items-center justify-between gap-4 border-b last:border-b-0 pb-3 last:pb-0">
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{hint}</p>
              </div>
              <Switch
                checked={flags[key]}
                onCheckedChange={(v) => setFlags((f) => ({ ...f, [key]: v }))}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Boleto pelo WhatsApp</CardTitle>
          <CardDescription>
            Boletos registrados no banco (Sicoob). O atendimento só lê boletos já emitidos — não emite nem baixa.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-4 border-b pb-3">
            <div>
              <p className="text-sm font-medium">2ª via no atendimento</p>
              <p className="text-xs text-muted-foreground">
                Quando o cliente pede &quot;boleto&quot; ou &quot;2ª via&quot;, o atendente virtual o identifica pelo número
                (pede o CPF/CNPJ se o número tiver mais de um cadastro) e envia o PDF, a linha digitável e o Pix.
              </p>
            </div>
            <Switch checked={boletoSegundaVia} onCheckedChange={setBoletoSegundaVia} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Lembrete antes do vencimento</p>
              <p className="text-xs text-muted-foreground">
                Envia a 2ª via do boleto em aberto nos dias escolhidos, entre 8h e 20h. Confere no banco antes e
                não envia se já foi pago. Usa o template <code>gestor_boleto_lembrete</code> (com o PDF); enquanto ele não
                existir na Meta, envia o aviso de cobrança já existente na conta (<code>gestor_cobranca_a_vencer</code> ou
                <code>gestor_cobranca_lembrete</code>), sem o PDF.
              </p>
            </div>
            <Switch checked={boletoLembrete} onCheckedChange={setBoletoLembrete} />
          </div>
          <div className="space-y-1 max-w-xs">
            <Label htmlFor="boleto-lembrete-dias">Dias antes do vencimento</Label>
            <Input
              id="boleto-lembrete-dias"
              value={boletoLembreteDias}
              onChange={(e) => setBoletoLembreteDias(e.target.value)}
              placeholder="2,0"
              disabled={!boletoLembrete}
            />
            <p className="text-xs text-muted-foreground">Separados por vírgula. 0 = no dia do vencimento.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Templates
          </CardTitle>
          <CardDescription>
            Cria os templates do Gestor na sua WABA. A aprovação pela Meta pode levar alguns minutos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={handleSeed} disabled={seedTemplates.isPending || !cfg?.temToken} className="gap-2">
            {seedTemplates.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Criar/atualizar templates na WABA
          </Button>
          {seedTemplates.data && (
            <Alert className="mt-4">
              <MessageSquare className="h-4 w-4" />
              <AlertTitle>
                {seedTemplates.data.criados} criados · {seedTemplates.data.jaExistiam} já existiam · {seedTemplates.data.falharam} falhas
              </AlertTitle>
              <AlertDescription>
                <ul className="mt-1 text-xs list-disc pl-4 space-y-0.5">
                  {seedTemplates.data.detalhes.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enviar teste</CardTitle>
          <CardDescription>Dispara o template aprovado <code>gestor_nota_autorizada</code> para validar o envio. Crie os templates antes (botão acima) e aguarde a aprovação da Meta.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-2">
          <div className="space-y-1 flex-1">
            <Label>Número (DDI+DDD+número)</Label>
            <Input value={numeroTeste} onChange={(e) => setNumeroTeste(e.target.value)} placeholder="5534998600650" />
          </div>
          <Button variant="outline" onClick={handleTestar} disabled={teste.isPending || !cfg?.ativo} className="gap-2">
            {teste.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Testar
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSalvar} disabled={salvar.isPending} className="gap-2">
          <Save className="h-4 w-4" />
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}
